import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  MinusCircle,
  FileCheck,
  Loader2,
  ChevronRight
} from 'lucide-react';
import { requirementsApi } from '../lib/api';
import { toast } from 'sonner';
import { cn, getCriticalityBadge, getComplianceStatusConfig, formatDate } from '../lib/utils';

const categories = [
  { value: 'governanca', label: 'Governança e Gestão de Riscos' },
  { value: 'medidas_tecnicas', label: 'Medidas Técnicas e Operacionais' },
  { value: 'gestao_incidentes', label: 'Gestão de Incidentes' },
  { value: 'continuidade_negocio', label: 'Continuidade de Negócio' },
  { value: 'cadeia_abastecimento', label: 'Cadeia de Abastecimento' },
  { value: 'avaliacao_eficacia', label: 'Avaliação de Eficácia' },
  { value: 'formacao', label: 'Formação em Cibersegurança' },
  { value: 'recursos_humanos', label: 'Recursos Humanos' },
  { value: 'gestao_ativos', label: 'Gestão de Ativos' },
];

const complianceStatuses = [
  { value: 'conforme', label: 'Conforme', icon: CheckCircle, color: 'text-green-600' },
  { value: 'nao_conforme', label: 'Não Conforme', icon: XCircle, color: 'text-red-600' },
  { value: 'em_progresso', label: 'Em Progresso', icon: Clock, color: 'text-blue-600' },
  { value: 'nao_aplicavel', label: 'Não Aplicável', icon: MinusCircle, color: 'text-slate-400' },
];

export default function Requirements() {
  const [requirements, setRequirements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedReq, setSelectedReq] = useState(null);
  const [showStatusDialog, setShowStatusDialog] = useState(false);

  useEffect(() => {
    loadRequirements();
  }, [categoryFilter]);

  const loadRequirements = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (categoryFilter) params.category = categoryFilter;
      const response = await requirementsApi.getAll(params);
      setRequirements(response.data);
    } catch (error) {
      toast.error('Erro ao carregar requisitos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    if (!selectedReq) return;
    try {
      await requirementsApi.updateStatus(selectedReq.requirement_id, status);
      toast.success('Estado atualizado com sucesso');
      setShowStatusDialog(false);
      loadRequirements();
    } catch (error) {
      toast.error('Erro ao atualizar estado');
    }
  };

  // Group by category
  const groupedRequirements = requirements.reduce((acc, req) => {
    const cat = req.category || 'outros';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(req);
    return acc;
  }, {});

  // Filter by search
  const filteredGroups = Object.entries(groupedRequirements).reduce((acc, [cat, reqs]) => {
    const filtered = reqs.filter(
      (req) =>
        req.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (filtered.length > 0) acc[cat] = filtered;
    return acc;
  }, {});

  // Calculate stats
  const stats = requirements.reduce(
    (acc, req) => {
      acc.total++;
      if (req.compliance_status === 'conforme') acc.compliant++;
      else if (req.compliance_status === 'em_progresso') acc.inProgress++;
      else if (req.compliance_status === 'nao_conforme') acc.nonCompliant++;
      else if (req.compliance_status === 'nao_aplicavel') acc.notApplicable++;
      else acc.notEvaluated++;
      return acc;
    },
    { total: 0, compliant: 0, inProgress: 0, nonCompliant: 0, notApplicable: 0, notEvaluated: 0 }
  );

  const compliancePercentage = stats.total > 0 
    ? Math.round((stats.compliant / (stats.total - stats.notApplicable)) * 100) || 0
    : 0;

  if (isLoading) {
    return (
      <Layout title="Requisitos NIS2">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Requisitos NIS2">
      <div className="space-y-6" data-testid="requirements-container">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="card-flat md:col-span-2">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-slate-500">Conformidade Geral</span>
                <span className="text-2xl font-bold text-slate-900">{compliancePercentage}%</span>
              </div>
              <Progress value={compliancePercentage} className="h-2" />
            </CardContent>
          </Card>
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.compliant}</p>
              <p className="text-xs text-slate-500">Conforme</p>
            </CardContent>
          </Card>
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
              <p className="text-xs text-slate-500">Em Progresso</p>
            </CardContent>
          </Card>
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{stats.nonCompliant}</p>
              <p className="text-xs text-slate-500">Não Conforme</p>
            </CardContent>
          </Card>
          <Card className="card-flat">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-slate-400">{stats.notEvaluated}</p>
              <p className="text-xs text-slate-500">Por Avaliar</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-flat">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Pesquisar requisitos..."
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-requirements-input"
                />
              </div>
              <Select
                value={categoryFilter}
                onValueChange={(value) => setCategoryFilter(value === 'all' ? '' : value)}
              >
                <SelectTrigger className="w-[250px]" data-testid="filter-category">
                  <SelectValue placeholder="Todas as Categorias" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Categorias</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requirements List by Category */}
        <Accordion type="multiple" defaultValue={Object.keys(filteredGroups)} className="space-y-4">
          {Object.entries(filteredGroups).map(([category, reqs]) => {
            const catInfo = categories.find((c) => c.value === category) || { label: category };
            const catStats = reqs.reduce(
              (acc, r) => {
                if (r.compliance_status === 'conforme') acc.compliant++;
                return acc;
              },
              { compliant: 0 }
            );

            return (
              <AccordionItem key={category} value={category} className="card-flat border rounded-lg">
                <AccordionTrigger className="px-4 py-3 hover:no-underline" data-testid={`category-${category}`}>
                  <div className="flex items-center justify-between w-full pr-4">
                    <div className="flex items-center gap-3">
                      <FileCheck className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-slate-900">{catInfo.label}</span>
                      <Badge variant="outline" className="text-xs">
                        {reqs.length} requisitos
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">
                        {catStats.compliant}/{reqs.length} conforme
                      </span>
                      <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${(catStats.compliant / reqs.length) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="border-t border-slate-100">
                    {reqs.map((req) => {
                      const critConfig = getCriticalityBadge(req.criticality);
                      const statusConfig = getComplianceStatusConfig(req.compliance_status);
                      const StatusIcon = complianceStatuses.find(s => s.value === req.compliance_status)?.icon || MinusCircle;

                      return (
                        <div
                          key={req.requirement_id}
                          className="flex items-center gap-4 px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50"
                          data-testid={`requirement-${req.requirement_id}`}
                        >
                          <StatusIcon className={cn('h-5 w-5 flex-shrink-0', statusConfig.color.replace('bg-', 'text-').replace('-50', '-600'))} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-xs text-slate-400">{req.code}</span>
                              <Badge variant="outline" className={cn('text-xs', critConfig.color)}>
                                {critConfig.label}
                              </Badge>
                            </div>
                            <p className="font-medium text-slate-900 truncate">{req.title}</p>
                            <p className="text-sm text-slate-500 line-clamp-1">{req.description}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className={cn('text-xs', statusConfig.color)}>
                              {statusConfig.label}
                            </Badge>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedReq(req);
                                setShowStatusDialog(true);
                              }}
                              data-testid={`update-status-${req.requirement_id}`}
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {Object.keys(filteredGroups).length === 0 && (
          <Card className="card-flat">
            <CardContent className="p-8 text-center">
              <FileCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Nenhum requisito encontrado</h3>
              <p className="text-sm text-slate-500">Tente ajustar os filtros de pesquisa</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Status Update Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atualizar Estado de Conformidade</DialogTitle>
            <DialogDescription>
              {selectedReq && (
                <div className="mt-2">
                  <p className="font-mono text-xs text-slate-400">{selectedReq.code}</p>
                  <p className="font-medium text-slate-900 mt-1">{selectedReq.title}</p>
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {complianceStatuses.map((status) => {
              const Icon = status.icon;
              const isSelected = selectedReq?.compliance_status === status.value;
              return (
                <Button
                  key={status.value}
                  variant={isSelected ? 'default' : 'outline'}
                  className={cn('justify-start', isSelected && 'ring-2 ring-blue-500')}
                  onClick={() => handleStatusChange(status.value)}
                  data-testid={`status-option-${status.value}`}
                >
                  <Icon className={cn('h-4 w-4 mr-2', status.color)} />
                  {status.label}
                </Button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
